/**
 * Backend transport for one visitor / one widget instance. Talks to
 * /v1/api/chat/* on the services backend.
 *
 *   - `start()`     POST /session  -> session_id + conversation_id
 *   - `history()`   GET  /history  -> hydrate the message list on reconnect
 *   - `existingHistory()` GET /history before any session: the open
 *                   conversation of a returning visitor, or nothing
 *   - `send(text)`  POST /message
 *   - `connect(cb)` GET  /stream   (SSE) -> bot/agent replies, calls `cb`
 *                   once per message. Reconnects exponentially, and
 *                   after a drop fetches /history for what it missed.
 */

/** Messages a reconnect catch-up asks /history for: the server's
 *  maximum. */
const HISTORY_CATCH_UP_LIMIT = 200;

export interface ChatMessage {
    id: string;
    role: "visitor" | "bot" | "agent" | "system";
    content: string;
    content_type: string;
    content_attributes: Record<string, unknown>;
    created_at?: string;
}

export interface ChatSessionParams {
    baseUrl: string;
    widgetToken: string;
    sourceId: string;
    locale?: string;
}

export class ChatSession {
    readonly #baseUrl: string;
    readonly #widgetToken: string;
    readonly #sourceId: string;
    readonly #locale?: string;
    #sessionId?: string;
    #conversationId?: string;
    #eventSource?: EventSource;
    #onMessage?: (msg: ChatMessage) => void;
    #reconnectAttempt = 0;
    #reconnectTimer?: number;
    /** The stream may have missed messages since it last opened: it went
     *  down, or a sender stopped waiting for it to open (opened()). */
    #catchUpOnOpen = false;
    /** The stream is open right now. */
    #open = false;
    /** Waiting in opened() for the stream to open. */
    #openWaiters: (() => void)[] = [];
    /** Ids of the messages the caller already has: what history()
     *  returned and what the stream delivered. */
    readonly #known = new Set<string>();

    constructor(params: ChatSessionParams) {
        this.#baseUrl = params.baseUrl;
        this.#widgetToken = params.widgetToken;
        this.#sourceId = params.sourceId;
        this.#locale = params.locale;
    }

    async start(): Promise<{ sessionId: string; conversationId: string }> {
        const res = await fetch(`${this.#baseUrl}/v1/api/chat/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                widget_token: this.#widgetToken,
                source_id: this.#sourceId,
                locale: this.#locale,
                user_agent: navigator.userAgent,
                referrer: document.referrer || undefined,
            }),
        });
        if (!res.ok) {
            throw new Error(`session start failed: HTTP ${res.status}`);
        }
        const body = await res.json();
        if (!body?.success || !body?.data) {
            throw new Error("session response malformed");
        }
        this.#sessionId = body.data.session_id;
        this.#conversationId = body.data.conversation_id;
        return {
            sessionId: this.#sessionId!,
            conversationId: this.#conversationId!,
        };
    }

    async history(): Promise<ChatMessage[]> {
        const messages = await this.#fetchHistory();
        // The caller shows these; a reconnect catch-up must not repeat them.
        for (const m of messages) this.#known.add(m.id);
        return messages;
    }

    /**
     * The visitor's open conversation, without starting a session first:
     * undefined when there is none (a visitor the server has never seen,
     * or whose last chat was closed). Creates nothing, so a page can show
     * a returning visitor their chat on load and the greeting to everyone
     * else. Asked with `optional=1`: no chat is then an answer, not a 404
     * in the console of the site.
     */
    async existingHistory(): Promise<ChatMessage[] | undefined> {
        const messages = await this.#requestHistory(undefined, true);
        for (const m of messages ?? []) this.#known.add(m.id);
        return messages;
    }

    /**
     * Resolves once the stream is open, or after `timeoutMs` at most. A
     * caller that goes ahead without it (sends a message) may get its
     * reply before the stream subscribes: the stream catches up on
     * /history when it does open.
     */
    opened(timeoutMs: number): Promise<void> {
        if (this.#open) return Promise.resolve();
        return new Promise((resolve) => {
            const done = (): void => {
                clearTimeout(timer);
                resolve();
            };
            const timer = window.setTimeout(() => {
                this.#openWaiters = this.#openWaiters.filter((w) => w !== done);
                this.#catchUpOnOpen = true;
                resolve();
            }, timeoutMs);
            this.#openWaiters.push(done);
        });
    }

    async send(text: string): Promise<void> {
        const res = await fetch(`${this.#baseUrl}/v1/api/chat/message`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                widget_token: this.#widgetToken,
                source_id: this.#sourceId,
                content: text,
            }),
        });
        // Surface HTTP failures (rate limit, auth, 5xx) to the caller —
        // otherwise the launcher's typing indicator spins forever on a
        // reply that will never come.
        if (!res.ok) {
            // The API returns { success: false, message } with a
            // localized, actionable explanation — a rate-limit reply
            // says how long to wait. Carry it so the launcher can show
            // it instead of a generic "try again", which is precisely
            // the wrong advice while a cooldown is running.
            let serverMessage: string | undefined;
            try {
                const body = (await res.json()) as { message?: unknown };
                if (typeof body?.message === "string" && body.message.trim()) {
                    serverMessage = body.message.trim();
                }
            } catch {
                // non-JSON body (proxy error page) — fall back below
            }
            const err = new Error(
                serverMessage ?? `message send failed: HTTP ${res.status}`,
            ) as Error & { status?: number; serverMessage?: string };
            err.status = res.status;
            err.serverMessage = serverMessage;
            throw err;
        }
    }

    connect(onMessage: (msg: ChatMessage) => void): void {
        this.#onMessage = onMessage;
        this.#openStream();
    }

    disconnect(): void {
        this.#onMessage = undefined;
        this.#open = false;
        if (this.#reconnectTimer !== undefined) {
            clearTimeout(this.#reconnectTimer);
            this.#reconnectTimer = undefined;
        }
        this.#eventSource?.close();
        this.#eventSource = undefined;
    }

    #openStream(): void {
        const url = new URL(`${this.#baseUrl}/v1/api/chat/stream`);
        url.searchParams.set("widget_token", this.#widgetToken);
        url.searchParams.set("source_id", this.#sourceId);
        const es = new EventSource(url.toString());
        this.#eventSource = es;

        es.addEventListener("open", () => {
            this.#open = true;
            const waiters = this.#openWaiters;
            this.#openWaiters = [];
            for (const done of waiters) done();
            // Connected again, so the next drop starts the backoff from
            // the bottom. Resetting only on a "message" let a long visit
            // with a few drops (and no reply in between) ratchet every
            // later reconnect up to 30 s.
            this.#reconnectAttempt = 0;
            if (this.#catchUpOnOpen) {
                this.#catchUpOnOpen = false;
                void this.#catchUp();
            }
        });

        es.addEventListener("message", (e) => {
            this.#reconnectAttempt = 0;
            try {
                const msg = JSON.parse((e as MessageEvent).data) as ChatMessage;
                this.#deliver(msg);
            } catch (err) {
                console.warn("AmaiChat: bad SSE message payload", err);
            }
        });

        es.addEventListener("error", () => {
            es.close();
            this.#eventSource = undefined;
            this.#open = false;
            if (!this.#onMessage) return;
            this.#catchUpOnOpen = true;
            // Reconnect with exponential backoff capped at 30s.
            this.#reconnectAttempt = Math.min(this.#reconnectAttempt + 1, 5);
            const delay = Math.min(
                1000 * Math.pow(2, this.#reconnectAttempt),
                30000,
            );
            this.#reconnectTimer = window.setTimeout(() => {
                this.#openStream();
            }, delay);
        });
    }

    /**
     * The server pushes only to a stream that is open at that moment:
     * whatever it sent while this one was down (the reconnect wait, a
     * phone tab the OS put to sleep while the bot was thinking) or not
     * open yet (a first message sent without waiting longer) exists only
     * in /history. Fetched after the stream is back, so anything
     * newer arrives over the stream; an id seen both ways is delivered
     * once. Visitor messages are left out, as on the stream.
     */
    async #catchUp(): Promise<void> {
        let messages: ChatMessage[];
        try {
            // The server's maximum: history is oldest first, and the
            // missed replies are the newest.
            messages = await this.#fetchHistory(HISTORY_CATCH_UP_LIMIT);
        } catch (err) {
            console.warn("AmaiChat: catching up after a reconnect failed", err);
            return;
        }
        for (const msg of messages) {
            if (msg.role !== "visitor") this.#deliver(msg);
        }
    }

    async #fetchHistory(limit?: number): Promise<ChatMessage[]> {
        return (await this.#requestHistory(limit)) ?? [];
    }

    /** undefined when the server has no conversation to show. */
    async #requestHistory(
        limit?: number,
        optional = false,
    ): Promise<ChatMessage[] | undefined> {
        const url = new URL(`${this.#baseUrl}/v1/api/chat/history`);
        url.searchParams.set("widget_token", this.#widgetToken);
        url.searchParams.set("source_id", this.#sourceId);
        if (limit !== undefined) url.searchParams.set("limit", String(limit));
        if (optional) url.searchParams.set("optional", "1");
        const res = await fetch(url.toString());
        if (!res.ok) return undefined;
        const body = await res.json();
        if (!body?.success || !body?.data?.messages) return undefined;
        return body.data.messages as ChatMessage[];
    }

    #deliver(msg: ChatMessage): void {
        if (!this.#onMessage) return;
        if (msg.id) {
            if (this.#known.has(msg.id)) return;
            this.#known.add(msg.id);
        }
        this.#onMessage(msg);
    }
}
